/* ===== SaaS panel: demo requests sent from the public landing ===== */

import { useCallback, useEffect, useState } from 'react'
import type { IdentityContext, SaasLead, SaasLeadStatus } from '@zetyar/data-contracts'
import { Inbox, RefreshCw } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Badge } from '@/presentation/components/ui/Badge'
import { Button } from '@/presentation/components/ui/Button'
import { Modal } from '@/presentation/components/ui/Modal'
import { webSaasLeadRepository } from '@/infrastructure/repositories/leads'
import { MEMBERS_ESTIMATE_OPTIONS, ORGANIZATION_TYPE_OPTIONS } from '@/presentation/pages/landing/leadSchema'

const STATUS_LABELS: Record<SaasLeadStatus, string> = {
  new: 'Nuevo',
  contacted: 'Contactado',
  qualified: 'Calificado',
  discarded: 'Descartado',
  converted: 'Convertido',
}

const STATUS_VARIANTS: Record<SaasLeadStatus, 'info' | 'primary' | 'warning' | 'neutral' | 'success'> = {
  new: 'info',
  contacted: 'primary',
  qualified: 'warning',
  discarded: 'neutral',
  converted: 'success',
}

const STATUSES = Object.keys(STATUS_LABELS) as SaasLeadStatus[]

const FIELD_CLASS =
  'h-10 rounded-[13px] border border-neutral-200 bg-white px-3 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100'

function labelOf(options: readonly { value: string; label: string }[], value: string): string {
  return options.find((option) => option.value === value)?.label ?? value
}

interface SaasLeadsCardProps {
  readonly context: IdentityContext | null
  readonly onNewCountChange?: (count: number) => void
}

export function SaasLeadsCard({ context, onNewCountChange }: SaasLeadsCardProps) {
  const [leads, setLeads] = useState<SaasLead[]>([])
  const [statusFilter, setStatusFilter] = useState<SaasLeadStatus | 'all'>('all')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<SaasLead | null>(null)
  const [draftStatus, setDraftStatus] = useState<SaasLeadStatus>('new')
  const [draftNotes, setDraftNotes] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Own loading/error state: a failure here must not blank the rest of the SaaS panel.
  const load = useCallback(async () => {
    const { listForPlatform, countNew } = webSaasLeadRepository
    if (!context || !listForPlatform) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const [rows, newCount] = await Promise.all([
        listForPlatform(context, { status: statusFilter === 'all' ? undefined : statusFilter, limit: 100 }),
        countNew ? countNew(context) : Promise.resolve(null),
      ])
      setLeads(rows)
      if (newCount !== null) onNewCountChange?.(newCount)
    } catch (caught) {
      console.error('Error al cargar solicitudes de demo:', caught)
      setError('No se pudieron cargar las solicitudes de demo.')
    } finally {
      setIsLoading(false)
    }
  }, [context, statusFilter, onNewCountChange])

  useEffect(() => {
    void load()
  }, [load])

  const openLead = (lead: SaasLead) => {
    setSelected(lead)
    setDraftStatus(lead.status)
    setDraftNotes(lead.notes ?? '')
    setSaveError(null)
  }

  const save = async () => {
    const update = webSaasLeadRepository.update
    if (!context || !selected || !update) return
    setIsSaving(true)
    setSaveError(null)
    try {
      await update(context, selected.id, { status: draftStatus, notes: draftNotes })
      setSelected(null)
      await load()
    } catch (caught) {
      console.error('Error al actualizar la solicitud de demo:', caught)
      setSaveError('No se pudo guardar el cambio.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex w-full flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Solicitudes de demo</CardTitle>
            <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
              Enviadas desde la landing pública. Solo el equipo Zetyar puede verlas.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              aria-label="Filtrar solicitudes por estado"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as SaasLeadStatus | 'all')}
              className={FIELD_CLASS}
            >
              <option value="all">Todos los estados</option>
              {STATUSES.map((status) => (
                <option key={status} value={status}>
                  {STATUS_LABELS[status]}
                </option>
              ))}
            </select>
            <Button variant="secondary" size="sm" leftIcon={<RefreshCw className="h-4 w-4" />} isLoading={isLoading} onClick={() => void load()}>
              Actualizar
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && <p role="alert" className="mb-3 text-sm text-danger-600 dark:text-danger-300">{error}</p>}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="border-b border-neutral-200 bg-neutral-50 font-semibold text-neutral-500 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-400">
              <tr>
                <th className="p-3">Recibida</th>
                <th className="p-3">Organización</th>
                <th className="p-3">Contacto</th>
                <th className="p-3">Tamaño</th>
                <th className="p-3">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {isLoading && leads.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-3 text-neutral-500 dark:text-neutral-400">
                    Cargando...
                  </td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-6">
                    <div className="flex flex-col items-center gap-2 text-neutral-500 dark:text-neutral-400">
                      <Inbox className="h-6 w-6" aria-hidden="true" />
                      Aún no hay solicitudes con este filtro.
                    </div>
                  </td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <tr
                    key={lead.id}
                    tabIndex={0}
                    className="cursor-pointer hover:bg-neutral-50/80 focus:bg-neutral-50 dark:hover:bg-neutral-950 dark:focus:bg-neutral-950"
                    onClick={() => openLead(lead)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        openLead(lead)
                      }
                    }}
                  >
                    <td className="whitespace-nowrap p-3 text-xs text-neutral-500 dark:text-neutral-400">
                      {new Date(lead.createdAt).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td className="p-3">
                      <p className="font-semibold text-neutral-900 dark:text-neutral-100">{lead.organizationName}</p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">{labelOf(ORGANIZATION_TYPE_OPTIONS, lead.organizationType)}</p>
                    </td>
                    <td className="p-3">
                      <p className="text-neutral-900 dark:text-neutral-100">{lead.fullName}</p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">{lead.email}</p>
                    </td>
                    <td className="p-3 text-neutral-600 dark:text-neutral-300">{labelOf(MEMBERS_ESTIMATE_OPTIONS, lead.membersEstimate)}</td>
                    <td className="p-3">
                      <Badge variant={STATUS_VARIANTS[lead.status]}>{STATUS_LABELS[lead.status]}</Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>

      <Modal
        isOpen={Boolean(selected)}
        onClose={() => {
          if (!isSaving) setSelected(null)
        }}
        title={selected?.organizationName ?? 'Solicitud de demo'}
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => setSelected(null)} disabled={isSaving}>
              Cerrar
            </Button>
            <Button onClick={() => void save()} isLoading={isSaving}>
              Guardar
            </Button>
          </>
        }
      >
        {selected && (
          <div className="space-y-4">
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <LeadDetail label="Contacto" value={selected.fullName} />
              <div>
                <dt className="text-xs text-neutral-500 dark:text-neutral-400">Correo</dt>
                <dd>
                  <a href={`mailto:${selected.email}`} className="font-medium text-primary-600 hover:underline dark:text-primary-300">
                    {selected.email}
                  </a>
                </dd>
              </div>
              <LeadDetail label="Teléfono" value={selected.phone ?? 'No informado'} />
              <LeadDetail label="Tipo de organización" value={labelOf(ORGANIZATION_TYPE_OPTIONS, selected.organizationType)} />
              <LeadDetail label="Cantidad de miembros" value={labelOf(MEMBERS_ESTIMATE_OPTIONS, selected.membersEstimate)} />
              <LeadDetail label="Recibida" value={new Date(selected.createdAt).toLocaleString('es-CL')} />
              <LeadDetail label="Consentimiento" value={new Date(selected.consentAt).toLocaleString('es-CL')} />
            </dl>
            {selected.message && (
              <div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">Mensaje</p>
                <p className="mt-1 whitespace-pre-wrap rounded-[13px] bg-neutral-50 p-3 text-sm text-neutral-800 dark:bg-neutral-800/60 dark:text-neutral-200">
                  {selected.message}
                </p>
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-[200px_1fr]">
              <label className="flex flex-col gap-1.5 text-sm font-medium text-neutral-700 dark:text-neutral-200">
                Estado
                <select value={draftStatus} onChange={(event) => setDraftStatus(event.target.value as SaasLeadStatus)} className={FIELD_CLASS}>
                  {STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {STATUS_LABELS[status]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1.5 text-sm font-medium text-neutral-700 dark:text-neutral-200">
                Notas internas
                <textarea
                  rows={3}
                  maxLength={2000}
                  value={draftNotes}
                  onChange={(event) => setDraftNotes(event.target.value)}
                  className="rounded-[13px] border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100"
                />
              </label>
            </div>
            {saveError && <p role="alert" className="text-sm text-danger-600 dark:text-danger-300">{saveError}</p>}
          </div>
        )}
      </Modal>
    </Card>
  )
}

function LeadDetail({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div>
      <dt className="text-xs text-neutral-500 dark:text-neutral-400">{label}</dt>
      <dd className="font-medium text-neutral-900 dark:text-neutral-100">{value}</dd>
    </div>
  )
}
